import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ArrowLeft } from "lucide-react";
import { Logo } from "../../components/marketing/Logo";
import { setToken, getToken, formatApiError } from "./api";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.title = "Admin Login | Kryvo CRM";
        if (getToken()) navigate("/admin", { replace: true });
    }, [navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
                { email, password },
            );
            setToken(data.token);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-login-page" className="bg-grid-faint flex min-h-screen items-center justify-center bg-[#F7FEF9] px-4">
            <div className="w-full max-w-sm rounded-3xl border border-[#E5E7EB] bg-white p-8 shadow-xl shadow-slate-900/[0.05]">
                <Link to="/" className="inline-flex" aria-label="Back to Kryvo home"><Logo /></Link>
                <h1 className="mt-6 flex items-center gap-2 font-display text-xl font-extrabold text-[#111827]">
                    <Lock className="h-4 w-4 text-[#16A34A]" /> Admin sign in
                </h1>
                <p className="mt-1.5 text-sm text-[#64748B]">Kryvo team access only.</p>
                <form onSubmit={submit} className="mt-6 grid gap-4">
                    <div className="grid gap-1.5">
                        <label htmlFor="admin-email" className="text-xs font-semibold text-[#334155]">Email</label>
                        <input
                            id="admin-email"
                            data-testid="admin-login-email"
                            type="email"
                            required
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded-lg border border-[#E5E7EB] px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#16A34A]"
                            placeholder="admin@kryvocrm.com"
                        />
                    </div>
                    <div className="grid gap-1.5">
                        <label htmlFor="admin-password" className="text-xs font-semibold text-[#334155]">Password</label>
                        <input
                            id="admin-password"
                            data-testid="admin-login-password"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="rounded-lg border border-[#E5E7EB] px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#16A34A]"
                            placeholder="••••••••"
                        />
                    </div>
                    {error && (
                        <p data-testid="admin-login-error" className="rounded-lg bg-red-50 px-3.5 py-2.5 text-xs font-semibold text-red-600">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={loading}
                        className="flex items-center justify-center gap-2 rounded-full bg-[#16A34A] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#15803D] disabled:opacity-60"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
                <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs font-medium text-[#94A3B8] hover:text-[#15803D]">
                    <ArrowLeft className="h-3 w-3" /> Back to website
                </Link>
            </div>
        </div>
    );
}
